import { usePageData } from "@/lib/pageData";
import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { CheckCircle2, Loader2 } from "lucide-react";
import { INDIA_STATES, INDIA_UNION_TERRITORIES, isIndiaRegion } from "@/lib/serviceAreas";
import { enquiryLocation, recordEnquirySuccess } from "@/lib/enquiryTracking";

const inputCls = "w-full rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-[#d4a017]";
const labelCls = "mb-1.5 block text-sm font-semibold text-slate-800";

export function EnquiryForm({ prefillService = "", prefillState = "", sourcePage = "", heading = "Request a Quote" }) {
  const initial = usePageData();
  const [services, setServices] = useState(initial?.services || []);
  const [stateRegion, setStateRegion] = useState(isIndiaRegion(prefillState) ? prefillState : "");
  const [form, setForm] = useState({
    full_name: "", mobile: "", email: "", business_name: "", city_state: "",
    service_slug: prefillService, message: "", consent: false, website: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (prefillService) setForm((f) => ({ ...f, service_slug: prefillService }));
  }, [prefillService]);
  useEffect(() => {
    if (isIndiaRegion(prefillState)) setStateRegion(prefillState);
  }, [prefillState]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const grouped = services.reduce((acc, s) => {
    (acc[s.category_name] = acc[s.category_name] || []).push(s);
    return acc;
  }, {});

  async function submit(e) {
    e.preventDefault();
    if (submitting || done) return;
    setError("");
    if (!isIndiaRegion(stateRegion)) {
      setError("Choose your state or union territory so we can review the applicable service requirements.");
      return;
    }
    const cityState = enquiryLocation(form.city_state, stateRegion);
    if (cityState.length > 120) {
      setError("Please shorten the city / district entry so the complete location fits within 120 characters.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/enquiries", {
        ...form,
        city_state: cityState,
        email: form.email || null,
        business_name: form.business_name || null,
        source_page: sourcePage || window.location.pathname,
      });
      setDone(true);
      recordEnquirySuccess({ service: form.service_slug, state: stateRegion, sourcePage });
    } catch (err) {
      setError(apiError(err, "We could not submit your enquiry right now. Please try again or reach us on WhatsApp — your details above are still here."));
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-8 text-center" data-testid="enquiry-success">
        <CheckCircle2 className="mx-auto text-emerald-600" size={44} />
        <h3 className="mt-3 font-heading text-xl font-bold text-emerald-900">Enquiry received</h3>
        <p className="mt-2 text-sm text-emerald-800">
          Thank you, {form.full_name.split(" ")[0]}. Your enquiry has been saved and our team will contact you on {form.mobile} shortly.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="enquiry-form" noValidate={false}>
      <h3 className="font-heading text-xl font-bold text-[#0a192f]">{heading}</h3>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="eq-name" className={labelCls}>Full Name *</label>
          <input id="eq-name" className={inputCls} value={form.full_name} onChange={set("full_name")} required minLength={2} maxLength={100}
                 autoComplete="name" placeholder="Your full name" data-testid="enquiry-name-input" />
        </div>
        <div>
          <label htmlFor="eq-mobile" className={labelCls}>Mobile Number *</label>
          <input id="eq-mobile" className={inputCls} value={form.mobile} onChange={set("mobile")} required inputMode="tel"
                 autoComplete="tel" placeholder="10-digit mobile number" data-testid="enquiry-mobile-input" />
        </div>
        <div>
          <label htmlFor="eq-email" className={labelCls}>Email <span className="font-normal text-slate-400">(optional)</span></label>
          <input id="eq-email" type="email" className={inputCls} value={form.email} onChange={set("email")} autoComplete="email"
                 placeholder="you@example.com" data-testid="enquiry-email-input" />
        </div>
        <div>
          <label htmlFor="eq-business" className={labelCls}>Business Name <span className="font-normal text-slate-400">(optional)</span></label>
          <input id="eq-business" className={inputCls} value={form.business_name} onChange={set("business_name")} maxLength={150}
                 autoComplete="organization" placeholder="Your business name" data-testid="enquiry-business-input" />
        </div>
        <div>
          <label htmlFor="eq-city" className={labelCls}>City / District *</label>
          <input id="eq-city" className={inputCls} value={form.city_state} onChange={set("city_state")} required minLength={2} maxLength={120}
                 autoComplete="address-level2" placeholder="e.g. Chennai" data-testid="enquiry-city-input" />
        </div>
        <div>
          <label htmlFor="eq-state" className={labelCls}>State / Union Territory *</label>
          <select id="eq-state" className={inputCls} value={stateRegion} onChange={(event) => setStateRegion(event.target.value)} required autoComplete="address-level1" data-testid="enquiry-state-select">
            <option value="">Select your state / union territory</option>
            <optgroup label="States">{INDIA_STATES.map((state) => <option key={state} value={state}>{state}</option>)}</optgroup>
            <optgroup label="Union territories">{INDIA_UNION_TERRITORIES.map((state) => <option key={state} value={state}>{state}</option>)}</optgroup>
          </select>
        </div>
        <div>
          <label htmlFor="eq-service" className={labelCls}>Required Service *</label>
          <select id="eq-service" className={inputCls} value={form.service_slug} onChange={set("service_slug")} required data-testid="enquiry-service-select">
            <option value="">Select a service</option>
            {Object.entries(grouped).map(([cat, list]) => (
              <optgroup key={cat} label={cat}>
                {list.map((s) => <option key={s.slug} value={s.slug}>{s.name}</option>)}
              </optgroup>
            ))}
          </select>
        </div>
      </div>
      <div>
        <label htmlFor="eq-message" className={labelCls}>Message</label>
        <textarea id="eq-message" rows={4} className={inputCls} value={form.message} onChange={set("message")} maxLength={2000}
                  placeholder="Briefly describe your requirement. Please do not share Aadhaar, PAN scans, passwords or other sensitive documents here."
                  data-testid="enquiry-message-input" />
      </div>
      <input type="text" name="website" value={form.website} onChange={set("website")} tabIndex={-1} autoComplete="off"
             className="absolute left-[-9999px] h-0 w-0 opacity-0" aria-hidden="true" />
      <label className="flex items-start gap-2.5 text-sm text-slate-700" data-testid="enquiry-consent-label">
        <input type="checkbox" checked={form.consent} onChange={set("consent")} required className="mt-1 h-4 w-4 accent-[#d4a017]" data-testid="enquiry-consent-checkbox" />
        I consent to be contacted by Mynavodayans Online Services Pvt. Ltd. about this enquiry via call, SMS or WhatsApp.
      </label>
      {error && (
        <p className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700" role="alert" data-testid="enquiry-error">
          {error}
        </p>
      )}
      <button type="submit" disabled={submitting} className="btn-gold w-full disabled:cursor-not-allowed disabled:opacity-60" data-testid="enquiry-submit-btn">
        {submitting ? <><Loader2 className="animate-spin" size={18} /> Submitting…</> : "Submit Enquiry"}
      </button>
      <p className="text-center text-xs text-slate-500">Your details are used only to respond to this enquiry. No documents are collected through this form.</p>
    </form>
  );
}
